import { useEffect, useRef } from "react";
import { DrawerId, useDrawer } from "../context/DrawerContext";

/**
 * Runs `onOpen` every time the drawer opens, including the first render of a list that mounts
 * already open. `wasOpen` is seeded false for that reason — to a list re-deriving its selection,
 * a fresh mount and a reopen are the same event. This is the opposite seeding from
 * `useConnectionTransition`, which seeds from the mount-time value precisely so it does not fire
 * on mount.
 *
 * Prefill hangs off this rather than off "the local selection is empty" so that clearing a field
 * leaves it cleared, and so the result does not depend on whether the drawer's exit transition
 * had time to unmount the list before it was reopened.
 */
export const useDrawerOpenReset = (drawerId: DrawerId, onOpen: () => void) => {
  const { isOpen } = useDrawer(drawerId);
  const wasOpen = useRef<boolean>(false);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (isOpen && !wasOpen.current) onOpenRef.current();
    wasOpen.current = isOpen;
  }, [isOpen]);
};

export default useDrawerOpenReset;
